import { AutoTrackingZoomRatio, DetectionObject } from '@EcamModel/model/PTZAutoTracking';
import { Divider, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import useMileSightPTZ, { SectionsCollapsePTZ } from '@pages/cameras/hook/useMilesightPTZ';
import color from '@theme/Colors';
import { ReactNode, useEffect } from 'react';
import { transformScheduleData } from 'src/helpers';
import {
    DataSectionHasSubItem,
    SectionHasSubItemHeader,
    SectionHasSubItemProps,
    SectionNotSubItem,
} from './BasicInfoMilesightCameraPTZ';

const detectionObjectMapping: { [key in DetectionObject]: string } = {
    [DetectionObject.None]: 'None',
    [DetectionObject.OnlyPerson]: 'Person',
    [DetectionObject.OnlyVehicle]: 'Vehicle',
    [DetectionObject.PersonAndVehicle]: 'Person and Vehicle',
};

const zoomModeMapping: { [key in AutoTrackingZoomRatio]: string } = {
    [AutoTrackingZoomRatio.Auto]: 'Auto mode',
    [AutoTrackingZoomRatio.Customize]: 'Customize',
};

type Props = {
    isOpenCollapse: { [key in SectionsCollapsePTZ]: boolean };
    handleToggleCollapse: (section: SectionsCollapsePTZ) => void;
};

export default function AutoTrackingMilesightsCameraPTZ(props: Props) {
    const handleEdit = () => {
        console.log('Edit button clicked');
    };
    const { camera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;
    const PTZAutoTracking = camera?.PTZCamera?.PTZCameraConfig?.AutoTracking;

    const statusList_AutoTrackingHeader: DataSectionHasSubItem[] = [
        {
            label: 'Enable:',
            value: <Typography>{PTZAutoTracking?.TrackEnable! ? 'Yes' : 'No'}</Typography>,
        },
        {
            label: 'Report to motion detection:',
            value: <Typography>{PTZAutoTracking?.ReportMotion! ? 'Yes' : 'No'}</Typography>,
        },
    ];

    const statusList_AutoTracking_BasicSettings: DataSectionHasSubItem[] = [
        {
            label: 'Show tracking:',
            value: <Typography>{PTZAutoTracking?.ShowAutoEnable! ? 'Yes' : 'No'}</Typography>,
        },
        {
            label: 'Sensitivity:',
            value: <Typography>{PTZAutoTracking?.Sensitivity!}</Typography>,
        },
        {
            label: 'Max. tracking time:',
            value: <Typography>{`${PTZAutoTracking?.MaxTime!} (5-300s)`}</Typography>,
        },
        {
            label: 'Tracking zoom ratio:',
            value: <Typography>{zoomModeMapping[PTZAutoTracking?.ZoomMode!]}</Typography>,
        },
        {
            label: 'Detection object:',
            value: <Typography>{detectionObjectMapping[PTZAutoTracking?.Object!]}</Typography>,
        },
    ];

    const dataSectionHasSubItems: ReactNode | DataSectionHasSubItem[] = transformScheduleData(
        PTZAutoTracking?.ScheduleList!,
        (dayName, schedule) => ({
            label: `${dayName}:`,
            value: schedule,
        })
    );

    const sectionHasSubItemProps: SectionHasSubItemProps = {
        title: '',
        data: dataSectionHasSubItems,
    };

    return (
        <Stack id="autoTracking_BasicSettings" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            {/* Basic Information */}
            <SectionHasSubItemHeader id="" title="Auto tracking" onEdit={handleEdit} />

            <Divider
                orientation="vertical"
                sx={{
                    borderBottomWidth: 1,
                    borderColor: '#E4E7EC',
                    mx: 2,
                }}
            />

            <Stack width={'100%'} padding={'16px 16px'} bgcolor={color.white} gap={2}>
                {statusList_AutoTrackingHeader.map((item, index) => {
                    return (
                        <Grid key={index} xs={12} container>
                            <Grid xs={5}>
                                <Typography>{item.label}</Typography>
                            </Grid>
                            <Grid xs={7}>{item.value}</Grid>
                        </Grid>
                    );
                })}
            </Stack>

            {/* Basic Settings */}
            <SectionNotSubItem
                header="Basic settings"
                statusList={statusList_AutoTracking_BasicSettings}
                isOpen={isOpenCollapse.autoTracking_BasicSettings}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.AutoTracking_BasicSettings);
                }}
            />

            {/* Schedule Settings */}
            <SectionNotSubItem
                name="autoTracking_ScheduleSettings"
                header="Schedule settings"
                statusList={sectionHasSubItemProps.data}
                isOpen={isOpenCollapse.autoTracking_ScheduleSettings}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.AutoTracking_ScheduleSettings);
                }}
            />
        </Stack>
    );
}
